import type { ReactElement } from "react";

import { SWReactIcons } from "@similarweb/icons";
import { TextCell } from "@similarweb/pro-logic-components";
import { Tooltip } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { NO_DATA_SIGN, NOT_AVAILABLE } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { renderTrafficChangeFactory } from "app/pages/sales-intelligence/common-components/table-cells/factories";

import type {
    EcommerceSignalColumnCellDto,
    EcommerceSignalDataRowItem,
} from "../../../models/ecommerce/EcommerceSignalColumnCellDto";
import type { NormalizedEcommerceSignalColumnValue } from "../../../types/columns";
import {
    getEcommerceApiRowPreviousTooltipText,
    getEcommerceTrendVisualFromApiDirection,
    getEcommerceTrendVisualFromSign,
    numericChangeToTrendSign,
} from "../../../utils/ecommerce/ecommerceSignalDataCellUtils";
import signalsI18nKeys from "../../../utils/i18n";

import EcommerceCellAlign from "./EcommerceCellAlign";
import { LegacyCellTooltipAnchor } from "./styles";

type Props = {
    value: NormalizedEcommerceSignalColumnValue;
};

const buildApiRowInner = (item: EcommerceSignalDataRowItem, index: number): ReactElement => {
    const { isPositive, isNegative, color } = getEcommerceTrendVisualFromApiDirection(item.direction);
    const displayText = item.new_value?.trim() || NOT_AVAILABLE;
    const hasDirection = isPositive || isNegative;

    return (
        <Flex
            alignItems="center"
            gap="4px"
            data-automation={`ecommerce-signal-data-cell-${index}`}
            data-automation-direction={isPositive ? "up" : isNegative ? "down" : "flat"}
        >
            {hasDirection ? (
                <SWReactIcons
                    iconName={isPositive ? "arrow-up" : "arrow-down"}
                    iconColor={color}
                />
            ) : null}
            <Text
                fontVariant="body2"
                color={color}
            >
                {displayText}
            </Text>
        </Flex>
    );
};

const wrapApiRowWithTooltip = (item: EcommerceSignalDataRowItem, inner: ReactElement): ReactElement => (
    <Tooltip
        data-automation="ecommerce-signal-data-cell-tooltip"
        placement="top"
        content={getEcommerceApiRowPreviousTooltipText(item)}
    >
        <Flex inline>{inner}</Flex>
    </Tooltip>
);

const renderApiRowItems = (items: readonly EcommerceSignalDataRowItem[]): ReactElement => {
    if (items.length === 0) {
        return (
            <TextCell>
                <EcommerceCellAlign>{NOT_AVAILABLE}</EcommerceCellAlign>
            </TextCell>
        );
    }

    if (items.length === 1) {
        const item = items[0];
        return (
            <TextCell>
                <EcommerceCellAlign>{wrapApiRowWithTooltip(item, buildApiRowInner(item, 0))}</EcommerceCellAlign>
            </TextCell>
        );
    }

    return (
        <TextCell>
            <EcommerceCellAlign>
                <Stack
                    gap="6px"
                    alignItems="flex-end"
                >
                    {items.map((item, index) => (
                        <Flex key={`${item.metric}-${index}`}>
                            {wrapApiRowWithTooltip(item, buildApiRowInner(item, index))}
                        </Flex>
                    ))}
                </Stack>
            </EcommerceCellAlign>
        </TextCell>
    );
};

const renderLegacyObjectCell = (dto: EcommerceSignalColumnCellDto): ReactElement => {
    const hasNumericChange = dto.change != null && dto.change !== 0;
    const { isPositive, isNegative, color } = getEcommerceTrendVisualFromSign(
        numericChangeToTrendSign(dto.change ?? 0),
    );

    const displayText =
        dto.currentDisplayValue?.trim() || (hasNumericChange ? String(dto.change) : "") || NOT_AVAILABLE;

    const content = (
        <Flex
            alignItems="center"
            gap="4px"
            data-automation="ecommerce-signal-data-cell"
            data-automation-direction={isPositive ? "up" : isNegative ? "down" : "flat"}
        >
            {hasNumericChange ? (
                <SWReactIcons
                    iconName={isPositive ? "arrow-up" : "arrow-down"}
                    iconColor={color}
                />
            ) : null}
            <Text
                fontVariant="body2"
                color={color}
            >
                {displayText}
            </Text>
        </Flex>
    );

    const keys = signalsI18nKeys.ecommerce.tableCellEcommerceTrendTooltip;
    const prevLegacy = dto.previousDisplayValue?.trim();
    const legacyTooltip = prevLegacy
        ? translate(keys.previousOnlineRevenueWas, { value: prevLegacy })
        : translate(keys.previousOnlineRevenueUnknown);

    return (
        <Tooltip
            data-automation="ecommerce-signal-data-cell-tooltip"
            placement="top"
            content={legacyTooltip}
        >
            <TextCell>
                <LegacyCellTooltipAnchor>
                    <EcommerceCellAlign>{content}</EcommerceCellAlign>
                </LegacyCellTooltipAnchor>
            </TextCell>
        </Tooltip>
    );
};

const EcommerceSignalDataCell = ({ value }: Props): ReactElement => {
    if (value === null || typeof value === "undefined") {
        return (
            <TextCell>
                <EcommerceCellAlign>{NO_DATA_SIGN}</EcommerceCellAlign>
            </TextCell>
        );
    }

    if (typeof value === "number") {
        return (
            <TextCell>
                <EcommerceCellAlign>{renderTrafficChangeFactory(value)}</EcommerceCellAlign>
            </TextCell>
        );
    }

    if (Array.isArray(value)) {
        return renderApiRowItems(value);
    }

    const legacyDto = value as EcommerceSignalColumnCellDto;
    const hasDisplay = Boolean(legacyDto.currentDisplayValue?.trim());
    const hasChange = legacyDto.change != null;

    if (!hasDisplay && !hasChange && !legacyDto.previousDisplayValue) {
        return (
            <TextCell>
                <EcommerceCellAlign>{NO_DATA_SIGN}</EcommerceCellAlign>
            </TextCell>
        );
    }

    return renderLegacyObjectCell(legacyDto);
};

export default EcommerceSignalDataCell;
